import { useEffect, useMemo, useState } from 'react';

export const FALLBACK_TEXT_COLOR = '#000000';

export function useTextDarkColor(): string {
  const [textDarkColor, setTextDarkColor] = useState(FALLBACK_TEXT_COLOR);

  useEffect(() => {
    const computedStyle = getComputedStyle(document.documentElement);
    const cssValue = computedStyle.getPropertyValue('--text-dark').trim() || FALLBACK_TEXT_COLOR;
    setTextDarkColor(cssValue);
  }, []);

  return textDarkColor;
}

// Shared color palette (10 columns x 4 rows)
export const COLOR_PALETTE = [
  // Row 1: Dark/saturated colors
  ['#C00000', '#FF0000', '#FFC000', '#FFFF00', '#92D050', '#00B050', '#00B0F0', '#0070C0', '#002060', '#7030A0'],
  // Row 2: Medium shades
  ['#F4B183', '#FF7C80', '#FFD966', '#FFFF99', '#C5E0B4', '#A9D18E', '#9DC3E6', '#8FAADC', '#B4A7D6', '#C27BA0'],
  // Row 3: Light shades
  ['#F8CBAD', '#FFCCCC', '#FFE699', '#FFFFCC', '#E2F0D9', '#C6EFCE', '#DEEAF6', '#D6DCE5', '#D9D2E9', '#EAD1DC'],
  // Row 4: Grayscale
  ['#FFFFFF', '#F2F2F2', '#D9D9D9', '#BFBFBF', '#A6A6A6', '#808080', '#595959', '#404040', '#262626', FALLBACK_TEXT_COLOR],
];

type ColorPickerProps = {
  selectedColor: string;
  onColorChange: (color: string) => void;
  title?: string;
  children?: React.ReactNode;
};

export function ColorPicker({
  selectedColor,
  onColorChange,
  title,
  children,
}: ColorPickerProps): React.JSX.Element {
  const textDarkColor = useTextDarkColor();

  const palette = useMemo(() => {
    return COLOR_PALETTE.map((row) =>
      row.map((color) => (color === FALLBACK_TEXT_COLOR ? textDarkColor : color))
    );
  }, [textDarkColor]);

  return (
    <div className="color-picker-container">
      {title && <div className="color-picker-header">{title}</div>}
      <div className="color-picker-grid">
        {palette.map((row, rowIndex) => (
          <div key={rowIndex} className="color-picker-row">
            {row.map((color) => (
              <button
                key={color}
                className={`color-picker-swatch ${selectedColor.toUpperCase() === color.toUpperCase() ? 'selected' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => onColorChange(color)}
                aria-label={`Select color ${color}`}
                type="button"
              />
            ))}
          </div>
        ))}
      </div>
      {children}
    </div>
  );
}
